
import React from "react";
import { Page, PagePlatform } from "@/types/page";
import { Button } from "@/components/ui/button";
import { 
  Copy, 
  Edit, 
  Trash2,
  Smartphone,
  Monitor,
  GripVertical,
  MoreVertical,
  Eye,
  EyeOff
} from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";

interface SortablePageCardProps {
  page: Page;
  onToggle: (id: string) => void;
  onEdit: (page: Page) => void;
  onDuplicate: (page: Page) => void;
  onDelete: (id: string) => void;
  onPlatformChange: (id: string, platform: PagePlatform) => void;
  onPositionChange: (id: string, position: any) => void; // Kept for compatibility but not used
  currentColumn: 'enabled' | 'disabled';
  isDragOverlay?: boolean;
  isDraggable?: boolean;
}

const SortablePageCard: React.FC<SortablePageCardProps> = ({
  page,
  onToggle,
  onEdit,
  onDuplicate,
  onDelete,
  onPlatformChange,
  currentColumn,
  isDragOverlay = false,
  isDraggable = true
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: page.id,
    disabled: isDragOverlay || !isDraggable
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  // Platform display components
  const getPlatformIcon = () => {
    switch (page.platform) {
      case "desktop": return <Monitor className="h-3 w-3" />;
      case "mobile": return <Smartphone className="h-3 w-3" />;
      default: return (
        <div className="flex items-center gap-1">
          <Monitor className="h-3 w-3" />
          <Smartphone className="h-3 w-3" />
        </div>
      );
    }
  };

  const getPlatformText = () => {
    switch (page.platform) {
      case "desktop": return "מחשב";
      case "mobile": return "נייד";
      default: return "כל המכשירים";
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-xl shadow-sm border border-gray-200 p-4 h-24 flex items-center gap-3 transition-all duration-200 hover:shadow-md ${
        isDragOverlay ? "shadow-xl rotate-1 scale-105 z-50" : ""
      } ${currentColumn === 'disabled' ? "opacity-70" : ""}`}
    >
      {/* Drag Handle - Only for enabled pages */}
      {isDraggable && currentColumn === 'enabled' && (
        <div 
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded" 
          {...attributes} 
          {...listeners}
        >
          <GripVertical className="h-4 w-4 text-gray-400" />
        </div>
      )}
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-medium text-base truncate pr-2 text-gray-900">
            {page.title}
          </h3>
          <Button
            variant={page.enabled ? "default" : "outline"}
            size="sm"
            onClick={() => onToggle(page.id)}
            className="h-7 px-3 text-xs flex items-center gap-1"
          >
            {page.enabled ? (
              <>
                <Eye className="h-3 w-3" />
                פעיל
              </>
            ) : (
              <>
                <EyeOff className="h-3 w-3" />
                מושבת
              </>
            )}
          </Button>
        </div>
        
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs bg-gray-50">
            {page.type}
          </Badge>
          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
            {getPlatformIcon()}
            <span className="ml-1">{getPlatformText()}</span>
          </Badge>
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(page)}
          className="h-8 w-8 p-0"
        >
          <Edit className="h-3 w-3" />
        </Button>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDuplicate(page)}
          className="h-8 w-8 p-0"
        >
          <Copy className="h-3 w-3" />
        </Button>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreVertical className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>הגדרות פלטפורמה</DropdownMenuLabel>
            <DropdownMenuSeparator />
            
            <DropdownMenuItem onClick={() => onPlatformChange(page.id, 'both')}>
              <div className="flex items-center mr-2">
                <Monitor className="h-4 w-4" />
                <Smartphone className="h-4 w-4" />
              </div>
              הצג בכל המכשירים
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onPlatformChange(page.id, 'desktop')}>
              <Monitor className="h-4 w-4 mr-2" />
              מחשב בלבד
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onPlatformChange(page.id, 'mobile')}>
              <Smartphone className="h-4 w-4 mr-2" />
              נייד בלבד
            </DropdownMenuItem>
            
            <DropdownMenuSeparator />
            
            <DropdownMenuItem 
              onClick={() => onDelete(page.id)}
              className="text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              מחק דף
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default SortablePageCard;
